import { appendLog } from './logStore.js';
import { reportDevice as apiReportDevice } from './api/services/deviceService.js';

function lunaRequest(uri, { method, parameters = {}, subscribe = false } = {}) {
  return new Promise((resolve) => {
    const svc = globalThis?.webOS?.service;
    if (!svc) return resolve(null);
    try {
      svc.request(uri, {
        method,
        parameters: { ...parameters, subscribe },
        onSuccess: (res) => resolve(res || {}),
        onFailure: () => resolve(null),
      });
    } catch {
      resolve(null);
    }
  });
}

async function getSystemInfo() {
  // Try tv.systemproperty
  const res = await lunaRequest('luna://com.webos.service.tv.systemproperty', {
    method: 'getSystemInfo',
    parameters: {
      keys: [
        'serialNumber',
        'SerialNumber',
        'modelName',
        'firmwareVersion',
        'sdkVersion',
        'productName',
      ],
    },
  });
  if (res) return res;
  return null;
}

/**
 * Parse idList from getIDs response (webOS Signage: LGUDID = LG Unique Device ID, UUID-style).
 * @see docs/guides/system-features/get-unique-device-id.html
 */
function parseIdList(res) {
  const list = res?.idList;
  if (!Array.isArray(list)) return {};
  const out = {};
  for (const item of list) {
    if (item?.idType && item?.idValue) {
      out[item.idType] = String(item.idValue).trim();
    }
  }
  return out;
}

async function getDeviceIds() {
  // Luna Service: returns idList with idType "LGUDID", idValue (webOS Signage 3.0+)
  const res = await lunaRequest('luna://com.webos.service.sm.deviceid', {
    method: 'getIDs',
    parameters: {},
  });
  if (res) {
    const byType = parseIdList(res);
    return {
      ...res,
      ...byType,
      lgudid: byType.LGUDID || res.lgudid,
      deviceId: byType.LGUDID || res.deviceId,
      sdid: res.sdid,
      smid: res.smid,
    };
  }
  const res2 = await lunaRequest('luna://com.webos.service.sm', {
    method: 'getIDs',
    parameters: {},
  });
  if (res2) return res2;
  return null;
}

async function getConnectionStatus() {
  const res = await lunaRequest('luna://com.webos.service.connectionmanager', {
    method: 'getStatus',
    parameters: {},
  });
  if (res) return res;
  return null;
}

function fallbackGeneratedId() {
  const KEY = 'didapor:device:genid';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `gen-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return `gen-${Date.now()}`;
  }
}

export async function collectDeviceInfo() {
  const info = {
    serialNumber: '',
    deviceId: '',
    lgudid: '', // LG Unique Device ID (webOS Signage 3.0+, from idList idType LGUDID)
    modelName: '',
    firmwareVersion: '',
    sdkVersion: '',
    productName: '',
    mac: { wired: '', wifi: '' },
  };

  try {
    const sys = await getSystemInfo();
    if (sys) {
      info.serialNumber = sys.serialNumber || sys.SerialNumber || info.serialNumber;
      info.modelName = sys.modelName || info.modelName;
      info.firmwareVersion = sys.firmwareVersion || info.firmwareVersion;
      info.sdkVersion = sys.sdkVersion || info.sdkVersion;
      info.productName = sys.productName || info.productName;
    }
  } catch {}

  try {
    const ids = await getDeviceIds();
    if (ids) {
      const lgudid = ids.lgudid || ids.LGUDID || '';
      if (lgudid) info.lgudid = lgudid;
      info.deviceId =
        lgudid || ids.deviceId || ids.sdid || ids.smid || info.deviceId;
      if (!info.serialNumber && ids.serialNumber) info.serialNumber = ids.serialNumber;
    }
  } catch {}

  try {
    const net = await getConnectionStatus();
    if (net) {
      const wired = net.wiredInfo?.macAddress || net.ethernet?.macAddress || '';
      const wifi = net.wifiInfo?.macAddress || net.wifi?.macAddress || '';
      info.mac = { wired: wired || info.mac.wired, wifi: wifi || info.mac.wifi };
    }
  } catch {}

  if (!info.deviceId) info.deviceId = info.serialNumber || fallbackGeneratedId();

  try {
    appendLog({ type: 'info', detail: { event: 'device-info', info } });
  } catch {}
  return info;
}

export async function reportDeviceInfo(info, url, extra = {}) {
  const payload = { ...info, ...extra, ts: new Date().toISOString() };
  const result = await apiReportDevice(url, payload);
  return result.ok
    ? { ok: true }
    : { ok: false, reason: result.error || 'request-failed', error: result.error };
}
